import { ModuleItem } from "../types/optionalSystems";

export interface ModuleSaveData {
  version: string;
  timestamp: number;
  moduleData: ModuleItem;
  optionalSystemsData?: any; // Optional systems data from the module
  metadata: {
    moduleName: string;
    description?: string;
  };
}

const CURRENT_VERSION = "1.0.0";

export const saveModuleDesign = (moduleData: ModuleItem, filename?: string): void => {
  try {
    const saveData: ModuleSaveData = {
      version: CURRENT_VERSION,
      timestamp: Date.now(),
      moduleData,
      optionalSystemsData: moduleData.moduleConfig?.optionalSystems, // Include optional systems data
      metadata: {
        moduleName: moduleData.name || "Unnamed Module",
        description: moduleData.description || undefined,
      },
    };

    const dataStr = JSON.stringify(saveData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });

    // Generate filename if not provided
    const moduleName = (moduleData.name || "module").replace(/[^a-zA-Z0-9]/g, '_');
    const defaultFilename = `${moduleName}_${new Date().toISOString().split('T')[0]}.TM2`;
    const finalFilename = filename || defaultFilename;

    // Create download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(dataBlob);
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Error saving module design:", error);
    alert("Error saving module design. Please try again.");
  }
};

export const loadModuleDesign = (): Promise<ModuleItem | null> => {
  return new Promise((resolve) => {
    try {
      // Create file input
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".TM2,.tm2,application/json";
      
      // Only set display style if it exists (for test environment)
      if (input.style) {
        input.style.display = "none";
      }

      input.onchange = (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) {
          // User cancelled or no file selected
          resolve(null);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const parsedData = JSON.parse(content);

            // Check if this is a single module file (our format)
            if (parsedData.moduleData) {
              const saveData: ModuleSaveData = parsedData;
              
              // Validate file format
              if (!saveData.version || !saveData.moduleData) {
                if (typeof window !== 'undefined' && window.alert) {
                  window.alert("Invalid file format. This doesn't appear to be a valid module design file.");
                }
                resolve(null);
                return;
              }

              // If the saved module has optionalSystemsData, merge it into moduleConfig
              if (saveData.optionalSystemsData && saveData.moduleData) {
                if (!saveData.moduleData.moduleConfig) {
                  saveData.moduleData.moduleConfig = {};
                }
                saveData.moduleData.moduleConfig.optionalSystems = saveData.optionalSystemsData;
              }

              resolve(saveData.moduleData);
              return;
            }

            // Check if this is a full ship/module design file
            if (parsedData.shipData && parsedData.metadata?.isModuleMode) {
              // Extract module information from the full ship design
              const shipData = parsedData.shipData;
              const optionalSystemsData = parsedData.optionalSystemsData;
              
              // Create a module from the ship data
              const moduleData: ModuleItem = {
                name: shipData.finalDetails?.name || shipData.shipDetails?.name || "Imported Module",
                tonnage: shipData.shipSize || shipData.size || 100,
                description: shipData.finalDetails?.description || shipData.shipDetails?.description || "Module imported from ship design",
                fitoutCost: shipData.shipStats?.totalCost || 0,
                fitoutWeight: shipData.shipStats?.totalWeight || 0,
                fitoutPower: shipData.shipStats?.totalPower || 0,
                installed: false,
                // Extract moduleConfig with optional systems
                moduleConfig: {
                  // Core systems (excluded from modules per Traveller rules)
                  // bridge: excluded
                  // powerPlant: excluded  
                  // drives: excluded
                  // structure: excluded
                  // armor: excluded
                  
                  // Systems that CAN be included in modules
                  computer: shipData.computer,
                  sensors: shipData.sensors,
                  software: shipData.software,
                  weapons: shipData.weapons || [],
                  weaponMounts: shipData.weaponMounts || [],
                  defensiveSystems: shipData.defensiveSystems || [],
                  ammunitionStorage: shipData.ammunitionStorage || [],
                  
                  // Drives (stored in weapons array for compatibility)
                  drives: shipData.drives || [],
                  
                  // Power Plant (modules can have their own power plants)
                  powerPlant: shipData.powerPlants.length > 0 ? shipData.powerPlants[0] : null,
                  
                  // Accommodations and crew
                  crew: shipData.crew,
                  staterooms: shipData.staterooms || [],
                  stateroomConfig: shipData.stateroomConfig,
                  
                  // Cargo and fuel
                  cargo: shipData.cargo,
                  fuel: shipData.fuel,
                  cargoSpace: shipData.cargoSpace,
                  fuelTanks: shipData.fuelTanks,
                  
                  // Optional systems from the optionalSystemsData
                  optionalSystems: optionalSystemsData ? [
                    ...(optionalSystemsData.drones || []),
                    ...(optionalSystemsData.external || []),
                    ...(optionalSystemsData.internal || []),
                    ...(optionalSystemsData.software || []),
                    ...(optionalSystemsData.fuel || []),
                    ...(optionalSystemsData.accommodations || []),
                    ...(optionalSystemsData.bridge || []),
                    ...(optionalSystemsData.cargo || []),
                    ...(optionalSystemsData.sensors || []),
                    ...(optionalSystemsData.commonAreas || []),
                  ] : undefined,
                  
                  // Module-specific stats
                  totalCost: shipData.shipStats?.totalCost || 0,
                  totalWeight: shipData.shipStats?.totalWeight || 0,
                  totalPower: shipData.shipStats?.totalPower || 0,
                  remainingTons: shipData.shipStats?.remainingTons || 0,
                  crewRequirements: shipData.shipStats?.crewRequirements || 0,
                  totalCrew: shipData.shipStats?.totalCrew || 0,
                  totalPassengers: shipData.shipStats?.totalPassengers || 0,
                  monthlyCost: shipData.shipStats?.monthlyCost || 0,
                },
              };

              resolve(moduleData);
              return;
            }

            // Check version compatibility (basic check for now)
            if (parsedData.version && parsedData.version !== CURRENT_VERSION) {
              const shouldContinue = window.confirm(
                `This file was saved with version ${parsedData.version}, but the current version is ${CURRENT_VERSION}. ` +
                "Loading it may cause compatibility issues. Do you want to continue?"
              );
              if (!shouldContinue) {
                resolve(null);
                return;
              }
            }

            // If neither format matches
            if (typeof window !== 'undefined' && window.alert) {
              window.alert("Invalid file format. This doesn't appear to be a valid module design file.");
            }
            resolve(null);
          } catch (error) {
            console.error("Error parsing module design file:", error);
            if (typeof window !== 'undefined' && window.alert) {
              window.alert("Error reading module design file. The file may be corrupted or in an invalid format.");
            }
            resolve(null);
          }
        };

        reader.onerror = () => {
          if (typeof window !== 'undefined' && window.alert) {
            window.alert("Error reading file. Please try again.");
          }
          resolve(null);
        };

        reader.readAsText(file);
      };

      // Add click event to handle cancellation
      input.onclick = () => {
        // Reset the input value to allow the same file to be selected again
        input.value = '';
      };

      // Add cancel event handler
      input.oncancel = () => {
        console.log("File selection cancelled by user");
        resolve(null);
      };

      // Trigger file selection
      if (document.body && typeof document.body.appendChild === 'function') {
        document.body.appendChild(input);
        input.click();
        
        // Clean up the input element after a short delay
        setTimeout(() => {
          if (document.body.contains(input)) {
            document.body.removeChild(input);
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Error setting up file load:", error);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert("Error setting up file load. Please try again.");
      }
      resolve(null);
    }
  });
};

export const getDefaultModuleFilename = (moduleData: ModuleItem): string => {
  const moduleName = (moduleData.name || "Module_Design").replace(/[^a-zA-Z0-9]/g, '_');
  const now = new Date();
  const dateStr = now.getFullYear().toString() + 
    (now.getMonth() + 1).toString().padStart(2, '0') + 
    now.getDate().toString().padStart(2, '0');
  const timeStr = now.getHours().toString().padStart(2, '0') + 
    now.getMinutes().toString().padStart(2, '0') + 
    now.getSeconds().toString().padStart(2, '0');
  return `${moduleName}_${dateStr}_${timeStr}.TM2`;
}; 